/**
 * Condition Clearing Service
 *
 * Frontend service for managing auto-clear rules via the admin UI.
 * Condition scanning and clearing is handled automatically by the
 * condition-auto-clear edge function triggered from encompass-webhook.
 */

import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';
import type {
  ConditionAutoClearRule,
  ConditionAutoClearRuleRow
} from '@/types/condition-clearing';
import { rowToAutoClearRule } from '@/types/condition-clearing';

const log = createComponentLogger('ConditionClearingService', 'condition-clearing');

class ConditionClearingService {
  /**
   * Get all auto-clear rules (reads directly from DB for admin UI)
   */
  async getRules(): Promise<ConditionAutoClearRule[]> {
    const { data, error } = await supabase
      .from('condition_auto_clear_rules')
      .select('*')
      .order('priority', { ascending: true });

    if (error) {
      log.error('Failed to fetch rules', {}, error);
      throw new Error('Failed to fetch auto-clear rules');
    }

    return (data || []).map((row: ConditionAutoClearRuleRow) => rowToAutoClearRule(row));
  }

  /**
   * Create a new rule
   */
  async createRule(rule: {
    name: string;
    description?: string;
    conditionMatchField?: string;
    conditionMatchStrategy?: string;
    conditionMatchPattern: string;
    documentMatchStrategy?: string;
    documentMatchPattern: string;
    documentRequiredStatus?: string;
    action?: string;
    requireConfirmation?: boolean;
    priority?: number;
    disclosureCheckEnabled?: boolean;
    disclosureTypeRequired?: string | null;
    disclosureRecipientStatus?: string | null;
    disclosureEventFieldName?: string | null;
    disclosureEventFieldOperator?: string | null;
    disclosureEventFieldValue?: string | null;
  }): Promise<ConditionAutoClearRule> {
    const { data, error } = await supabase
      .from('condition_auto_clear_rules')
      .insert({
        name: rule.name,
        description: rule.description || null,
        condition_match_field: rule.conditionMatchField || 'title',
        condition_match_strategy: rule.conditionMatchStrategy || 'contains',
        condition_match_pattern: rule.conditionMatchPattern,
        document_match_strategy: rule.documentMatchStrategy || 'contains',
        document_match_pattern: rule.documentMatchPattern,
        document_required_status: rule.documentRequiredStatus || 'received',
        action: rule.action || 'clear',
        require_confirmation: rule.requireConfirmation ?? true,
        priority: rule.priority ?? 100,
        disclosure_check_enabled: rule.disclosureCheckEnabled ?? false,
        disclosure_type_required: rule.disclosureTypeRequired ?? null,
        disclosure_recipient_status: rule.disclosureRecipientStatus ?? null,
        disclosure_event_field_name: rule.disclosureEventFieldName ?? null,
        disclosure_event_field_operator: rule.disclosureEventFieldOperator ?? null,
        disclosure_event_field_value: rule.disclosureEventFieldValue ?? null
      })
      .select()
      .single();

    if (error) {
      log.error('Failed to create rule', { ruleName: rule.name }, error);
      throw new Error('Failed to create rule');
    }

    return rowToAutoClearRule(data as ConditionAutoClearRuleRow);
  }

  /**
   * Update an existing rule
   */
  async updateRule(
    id: string,
    updates: Partial<{
      name: string;
      description: string | null;
      conditionMatchField: string;
      conditionMatchStrategy: string;
      conditionMatchPattern: string;
      documentMatchStrategy: string;
      documentMatchPattern: string;
      documentRequiredStatus: string;
      action: string;
      requireConfirmation: boolean;
      priority: number;
      isActive: boolean;
      disclosureCheckEnabled: boolean;
      disclosureTypeRequired: string | null;
      disclosureRecipientStatus: string | null;
      disclosureEventFieldName: string | null;
      disclosureEventFieldOperator: string | null;
      disclosureEventFieldValue: string | null;
    }>
  ): Promise<ConditionAutoClearRule> {
    const dbUpdates: Record<string, unknown> = { updated_at: new Date().toISOString() };
    if (updates.name !== undefined) {
dbUpdates.name = updates.name;
}
    if (updates.description !== undefined) {
dbUpdates.description = updates.description;
}
    if (updates.conditionMatchField !== undefined) {
dbUpdates.condition_match_field = updates.conditionMatchField;
}
    if (updates.conditionMatchStrategy !== undefined) {
dbUpdates.condition_match_strategy = updates.conditionMatchStrategy;
}
    if (updates.conditionMatchPattern !== undefined) {
dbUpdates.condition_match_pattern = updates.conditionMatchPattern;
}
    if (updates.documentMatchStrategy !== undefined) {
dbUpdates.document_match_strategy = updates.documentMatchStrategy;
}
    if (updates.documentMatchPattern !== undefined) {
dbUpdates.document_match_pattern = updates.documentMatchPattern;
}
    if (updates.documentRequiredStatus !== undefined) {
dbUpdates.document_required_status = updates.documentRequiredStatus;
}
    if (updates.action !== undefined) {
dbUpdates.action = updates.action;
}
    if (updates.requireConfirmation !== undefined) {
dbUpdates.require_confirmation = updates.requireConfirmation;
}
    if (updates.priority !== undefined) {
dbUpdates.priority = updates.priority;
}
    if (updates.isActive !== undefined) {
dbUpdates.is_active = updates.isActive;
}
    if (updates.disclosureCheckEnabled !== undefined) {
dbUpdates.disclosure_check_enabled = updates.disclosureCheckEnabled;
}
    if (updates.disclosureTypeRequired !== undefined) {
dbUpdates.disclosure_type_required = updates.disclosureTypeRequired;
}
    if (updates.disclosureRecipientStatus !== undefined) {
dbUpdates.disclosure_recipient_status = updates.disclosureRecipientStatus;
}
    if (updates.disclosureEventFieldName !== undefined) {
dbUpdates.disclosure_event_field_name = updates.disclosureEventFieldName;
}
    if (updates.disclosureEventFieldOperator !== undefined) {
dbUpdates.disclosure_event_field_operator = updates.disclosureEventFieldOperator;
}
    if (updates.disclosureEventFieldValue !== undefined) {
dbUpdates.disclosure_event_field_value = updates.disclosureEventFieldValue;
}

    const { data, error } = await supabase
      .from('condition_auto_clear_rules')
      .update(dbUpdates)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      log.error('Failed to update rule', { ruleId: id }, error);
      throw new Error('Failed to update rule');
    }

    return rowToAutoClearRule(data as ConditionAutoClearRuleRow);
  }

  /**
   * Toggle a rule's active status
   */
  async toggleRule(id: string, isActive: boolean): Promise<void> {
    const { error } = await supabase
      .from('condition_auto_clear_rules')
      .update({ is_active: isActive, updated_at: new Date().toISOString() })
      .eq('id', id);

    if (error) {
      log.error('Failed to toggle rule', { ruleId: id }, error);
      throw new Error('Failed to toggle rule');
    }
  }

  /**
   * Delete a rule
   */
  async deleteRule(id: string): Promise<void> {
    const { error } = await supabase
      .from('condition_auto_clear_rules')
      .delete()
      .eq('id', id);

    if (error) {
      log.error('Failed to delete rule', { ruleId: id }, error);
      throw new Error('Failed to delete rule');
    }
  }
}

export const conditionClearingService = new ConditionClearingService();
